import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ShareDeviceReservationDialog } from '@/components/elements/ShareDeviceDialog';
import { ShareDeviceDialogContentNoAvailableSlots } from '@/components/elements/ShareDeviceDialogContentNoAvailableSlots';
import { ShareDeviceSidebar } from '@/components/elements/ShareDeviceSidebar';
import { TimeSelectionNotice } from '@/components/elements/ShareDeviceTimeSelectionNotice';
import { TimeSlotPicker } from '@/components/elements/ShareDeviceTimeSlotPicker';
import { SharedDeviceTimeoutNotification } from '@/components/elements/SharedDeviceTimeoutNotification';
import { ShareDeviceLayout } from '@/components/layouts/ShareDeviceLayout';
import { MESSAGE_KEY } from '@/constants/dialog';
import { ROUTE_PATH } from '@/constants/routes';
import { SHARED_DEVICE_TIMEOUT_SECONDS } from '@/constants/sharedDevice';
import { useDialog } from '@/hooks/useDialog';
import { useSharedDeviceTimeout } from '@/hooks/useSharedDeviceTimeout';
import { useTimeSlots } from '@/hooks/useTimeSlots';

export const ShareDeviceTimeSelection = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // カスタムフックで時間スロットデータを取得
  const {
    groupedTimeSlots,
    isRequesting,
    error,
    hasLoaded,
    hasDisplayableSlots,
  } = useTimeSlots();

  // TOP画面に戻るタイムアウト機能
  const { remainingSeconds, resetTimer } = useSharedDeviceTimeout(
    SHARED_DEVICE_TIMEOUT_SECONDS.DEFAULT,
  );

  // 満室ダイアログの制御
  const { dialogRef: fullyBookedDialogRef, open: openFullyBookedDialog } =
    useDialog();

  // 営業時間外ダイアログの制御
  const {
    dialogRef: outsideBusinessHoursDialogRef,
    open: openOutsideBusinessHoursDialog,
  } = useDialog();

  // ダイアログを開いたかどうかを追跡するref
  const hasOpenedDialogRef = useRef(false);

  // 予約可能なスロットが存在するか
  const hasAvailableSlots = groupedTimeSlots.some((dateGroup) =>
    dateGroup.hours.some((hourGroup) =>
      hourGroup.slots.some(
        (slot) => slot.displayable !== false && slot.available,
      ),
    ),
  );

  // 満室判定: 営業時間内のスロットは存在するが、予約可能なスロットがない
  const isFullyBooked =
    hasLoaded && !error && hasDisplayableSlots && !hasAvailableSlots;

  // 営業時間外判定: 営業時間内のスロットが1つも存在しない
  const isOutsideBusinessHours = hasLoaded && !error && !hasDisplayableSlots;

  // 満室または営業時間外の場合はダイアログを表示（一度だけ）
  useEffect(() => {
    // すでにダイアログを開いている場合は何もしない
    if (hasOpenedDialogRef.current) {
      return;
    }

    if (hasLoaded && isFullyBooked) {
      openFullyBookedDialog();
      hasOpenedDialogRef.current = true;
    } else if (hasLoaded && isOutsideBusinessHours) {
      openOutsideBusinessHoursDialog();
      hasOpenedDialogRef.current = true;
    }
  }, [
    isFullyBooked,
    isOutsideBusinessHours,
    hasLoaded,
    openFullyBookedDialog,
    openOutsideBusinessHoursDialog,
  ]);

  const handleBackToTop = () => {
    navigate(ROUTE_PATH.SHARE_DEVICE);
  };

  const handleTimeSelect = (datetime: string) => {
    navigate(ROUTE_PATH.SHARE_DEVICE_USER_INFO, {
      state: {
        selectedTime: datetime,
      },
    });
  };

  return (
    <ShareDeviceLayout showBackToTop onBackToTop={handleBackToTop}>
      <div className="page page_f2">
        <main className="page__main">
          <div className="page__content">
            <section className="f2">
              <div className="f2__line">
                <div className="f2__inner">
                  <div className="f2__area">
                    <TimeSelectionNotice />

                    {isRequesting && (
                      <div className="text-center py-8">
                        {t('shareDevice.timeSelection.loading')}
                      </div>
                    )}

                    {error && (
                      <div className="text-center py-8 text-red-500">
                        {error}
                      </div>
                    )}

                    {!isRequesting && !error && (
                      <TimeSlotPicker
                        groupedTimeSlots={groupedTimeSlots}
                        onTimeSelect={handleTimeSelect}
                      />
                    )}
                  </div>
                </div>
              </div>
            </section>
          </div>
        </main>

        <ShareDeviceSidebar />
      </div>

      {/* タイムアウト警告通知 */}
      <SharedDeviceTimeoutNotification
        remainingSeconds={remainingSeconds}
        onContinue={resetTimer}
      />

      {/* 満室ダイアログ */}
      <ShareDeviceReservationDialog ref={fullyBookedDialogRef}>
        <ShareDeviceDialogContentNoAvailableSlots
          onConfirm={handleBackToTop}
          messageKey={MESSAGE_KEY.FULLY_BOOKED}
        />
      </ShareDeviceReservationDialog>

      {/* 営業時間外ダイアログ */}
      <ShareDeviceReservationDialog ref={outsideBusinessHoursDialogRef}>
        <ShareDeviceDialogContentNoAvailableSlots
          onConfirm={handleBackToTop}
          messageKey={MESSAGE_KEY.OUTSIDE_BUSINESS_HOURS}
        />
      </ShareDeviceReservationDialog>
    </ShareDeviceLayout>
  );
};
